import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, Inbox, Users, FileBarChart2,
  Store, ShieldCheck, LogOut, Bell, Settings, Map as MapIcon, ChevronDown,
  TrendingUp, Ticket, PanelLeftClose, PanelLeftOpen, MapPin, User, Key, Building2,
  Database, HardDrive, ClipboardList, Lock, ShieldAlert,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import NotificationBell from "../components/NotificationBell";
import { ProfileModal, PasswordModal } from "../components/ProfileModals";
import useIdleLogout from "../lib/useIdleLogout";

const NAV = {
  KPPG: [
    { to: "/dashboard", label: "Dashboard Utama", icon: LayoutDashboard },
    { to: "/peta", label: "Peta Distribusi", icon: MapIcon },
    { to: "/monitoring-komoditas", label: "Monitoring Komoditas", icon: MapPin },
    { to: "/suppliers", label: "Supplier & Katalog", icon: Store },
    { to: "/purchase-orders", label: "Daftar Pesanan", icon: ClipboardList },
    { to: "/forecasting", label: "Forecasting Harga", icon: TrendingUp },
    { to: "/tickets", label: "Bantuan", icon: Ticket },
    { to: "/laporan", label: "Laporan & Analisis", icon: FileBarChart2 },
    { to: "/users", label: "Manajemen User", icon: Users },
  ],
  SPPG: [
    { to: "/dashboard", label: "Dashboard Utama", icon: LayoutDashboard },
    { to: "/peta", label: "Peta Distribusi", icon: MapIcon },
    { to: "/suppliers", label: "Supplier & Katalog", icon: Store },
    { to: "/po-buat", label: "Buat Pesanan", icon: ShoppingCart },
    { to: "/purchase-orders", label: "Daftar Pesanan", icon: ClipboardList },
    { to: "/tickets", label: "Bantuan", icon: Ticket },
    { to: "/profil-sppg", label: "Profil SPPG", icon: Building2 },
  ],
  Supplier: [
    { to: "/dashboard", label: "Dashboard Supplier", icon: LayoutDashboard },
    // Dua menu ini terkunci sampai akun supplier diverifikasi KPPG/Pemda.
    { to: "/produk", label: "Kelola Produk", icon: Package, butuhVerifikasi: true },
    { to: "/po-masuk", label: "Pesanan Masuk", icon: Inbox, butuhVerifikasi: true },
    { to: "/profil-supplier", label: "Profil Usaha", icon: Store },
    { to: "/tickets", label: "Bantuan", icon: Ticket },
  ],
  TPID: [
    { to: "/dashboard", label: "Dashboard Utama", icon: LayoutDashboard },
    { to: "/peta", label: "Peta Distribusi", icon: MapIcon },
    { to: "/monitoring-komoditas", label: "Monitoring Komoditas", icon: MapPin },
    { to: "/suppliers", label: "Supplier & Katalog", icon: Store },
    { to: "/purchase-orders", label: "Daftar Pesanan", icon: ClipboardList },
    { to: "/forecasting", label: "Forecasting Harga", icon: TrendingUp },
    { to: "/tickets", label: "Bantuan", icon: Ticket },
    { to: "/laporan", label: "Laporan & Analisis", icon: FileBarChart2 },
  ],
  Pemda: [
    { to: "/dashboard", label: "Dashboard Pemda", icon: LayoutDashboard },
    { to: "/peta", label: "Peta Distribusi", icon: MapIcon },
    { to: "/suppliers", label: "Supplier & Katalog", icon: Store },
    { to: "/purchase-orders", label: "Daftar Pesanan", icon: ClipboardList },
    { to: "/verifikasi-supplier", label: "Verifikasi Supplier", icon: ShieldCheck },
    { to: "/laporan", label: "Laporan & Analisis", icon: FileBarChart2 },
    { to: "/profil-pemda", label: "Profil Pemda", icon: Building2 },
  ],
  Pemprov: [
    { to: "/dashboard", label: "Dashboard Pemprov", icon: LayoutDashboard },
    { to: "/peta", label: "Peta Distribusi", icon: MapIcon },
    { to: "/suppliers", label: "Supplier & Katalog", icon: Store },
    { to: "/laporan", label: "Laporan & Analisis", icon: FileBarChart2 },
    { to: "/profil-pemprov", label: "Profil Pemprov", icon: Building2 },
  ],
};

const ROLE_COLOR = {
  KPPG: "bg-blue-100 text-blue-800 border-blue-200",
  SPPG: "bg-amber-100 text-amber-800 border-amber-200",
  Supplier: "bg-emerald-100 text-emerald-800 border-emerald-200",
  TPID: "bg-violet-100 text-violet-800 border-violet-200",
  Pemda: "bg-pink-100 text-pink-800 border-pink-200",
  Pemprov: "bg-teal-100 text-teal-800 border-teal-200",
};

const SIDEBAR_W = 256; // 16rem = w-64
const SIDEBAR_COLLAPSED_W = 72; // w-[72px]

export default function AppLayout() {
  const { user, ready, logout, features } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("sidebar_collapsed", collapsed ? "1" : "0"); } catch { }
  }, [collapsed]);

  // Sesi ditutup otomatis bila pengguna diam terlalu lama.
  useIdleLogout(Boolean(user && user.id));

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
        <div className="text-sm text-slate-500">Memuat sesi...</div>
      </div>
    );
  }
  if (!user || !user.id) return <Navigate to="/login" replace />;

  // Supplier yang belum diverifikasi hanya boleh membuka Dashboard, Profil
  // Usaha, dan Bantuan.
  const menungguVerifikasi = user.role === "Supplier" && user.supplier_verified === false;
  const items = (NAV[user.role] || []).filter(it => {
    if (it.to === "/forecasting" && !features.forecasting) return false;
    return true;
  });
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W;

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        className="bg-white border-r border-slate-200 flex flex-col fixed inset-y-0 left-0 z-30 print:hidden"
        style={{ width: sidebarWidth, transition: "width 200ms ease-in-out" }}
        data-testid="app-sidebar"
      >
        {/* Logo header */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-200 overflow-hidden">
          <div className="w-9 h-9 rounded-md bg-[#045498] flex items-center justify-center text-white shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 leading-none whitespace-nowrap">KEDAN MBG</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5 whitespace-nowrap">Sumatera Utara</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-5 space-y-1 overflow-y-auto overflow-x-hidden">
          {!collapsed && <div className="kedan-label px-3 mb-2 text-slate-400">Menu</div>}
          {items.map((it) => {
            const terkunci = it.butuhVerifikasi && menungguVerifikasi;
            if (terkunci) {
              return (
                <div
                  key={it.to}
                  className={`sidebar-link cursor-not-allowed !text-slate-300 hover:!bg-transparent ${collapsed ? "sidebar-link-collapsed" : ""}`}
                  title={`${it.label} — menunggu verifikasi akun`}
                  aria-disabled="true"
                  data-testid={`nav-${it.to.replace("/", "")}-locked`}
                >
                  <it.icon className="w-4 h-4 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="truncate">{it.label}</span>
                      <Lock className="w-3.5 h-3.5 shrink-0 ml-auto" />
                    </>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "sidebar-link-collapsed" : ""}`
                }
                title={collapsed ? it.label : undefined}
                data-testid={`nav-${it.to.replace("/", "")}`}
              >
                <it.icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="truncate">{it.label}</span>}
              </NavLink>
            );
          })}

          {/* Admin-only menu */}
          {user.is_admin && (
            <>
              {!collapsed && <div className="kedan-label px-3 mt-5 mb-2 text-slate-400">Admin</div>}
              {collapsed && <div className="border-t border-slate-200 my-3" />}
              <NavLink
                to="/master-data"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "sidebar-link-collapsed" : ""}`
                }
                title={collapsed ? "Master Data" : undefined}
                data-testid="nav-master-data"
              >
                <Database className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="truncate">Master Data</span>}
              </NavLink>
              <NavLink
                to="/backup-database"
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "sidebar-link-collapsed" : ""}`
                }
                title={collapsed ? "Backup Database" : undefined}
                data-testid="nav-backup-database"
              >
                <HardDrive className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="truncate">Backup Database</span>}
              </NavLink>
            </>
          )}
        </nav>

        {/* Collapse toggle + logout */}
        <div className="border-t border-slate-200">
          <div className="p-2">
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="sidebar-link w-full text-left text-slate-500 hover:!text-[#045498]"
              title={collapsed ? "Perbesar sidebar" : "Perkecil sidebar"}
              data-testid="sidebar-toggle"
            >
              {collapsed
                ? <PanelLeftOpen className="w-4 h-4 shrink-0" />
                : <PanelLeftClose className="w-4 h-4 shrink-0" />
              }
              {!collapsed && <span>Perkecil</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div
        className="flex-1 flex flex-col min-h-screen print:!ml-0"
        style={{ marginLeft: sidebarWidth, transition: "margin-left 200ms ease-in-out" }}
      >
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-20 flex items-center px-8 justify-between print:hidden">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#045498] transition-colors"
              title={collapsed ? "Perbesar sidebar" : "Perkecil sidebar"}
              data-testid="header-sidebar-toggle"
            >
              {collapsed
                ? <PanelLeftOpen className="w-5 h-5" />
                : <PanelLeftClose className="w-5 h-5" />
              }
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Dashboard</div>
              <div className="text-sm font-medium text-slate-700">Selamat datang kembali, {user.name?.split(" ")[0]}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {menungguVerifikasi && (
              <button
                onClick={() => navigate("/profil-supplier")}
                className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-amber-800 hover:bg-amber-100 transition-colors"
                title="Akun Anda menunggu verifikasi KPPG/Pemda. Klik untuk melengkapi dokumen pada Profil Usaha."
                data-testid="badge-menunggu-verifikasi"
              >
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <span className="text-xs font-semibold hidden sm:inline">Menunggu Verifikasi</span>
              </button>
            )}
            <NotificationBell />
            <div className="h-8 w-px bg-slate-200" />
            <div className="relative">
              <button
                className="flex items-center gap-3 hover:bg-slate-50 p-1.5 rounded-lg transition-colors text-left"
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                data-testid="header-user-btn"
              >
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-medium text-slate-900 leading-tight">{user.name}</div>
                  <div className={`kedan-badge mt-0.5 ${ROLE_COLOR[user.role]}`}>{user.role}</div>
                </div>
                {user.avatar ? (
                  <img src={user.avatar} alt="Avatar" className="w-9 h-9 rounded-full object-cover shrink-0 border border-slate-200" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#045498] text-white flex items-center justify-center font-display font-semibold text-sm shrink-0">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
              </button>

              {profileMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setProfileMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden animate-[fadeIn_0.1s_ease-out]">
                    <div className="py-1">
                      <div className="px-4 py-3 border-b border-slate-100 sm:hidden">
                        <div className="text-sm font-medium text-slate-900">{user.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{user.role}</div>
                      </div>
                      <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => { setProfileMenuOpen(false); setShowProfileModal(true); }}>
                        <User className="w-4 h-4 text-slate-400 shrink-0" /> Ubah Profil
                      </button>
                      <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => { setProfileMenuOpen(false); setShowPasswordModal(true); }}>
                        <Key className="w-4 h-4 text-slate-400 shrink-0" /> Ganti Password
                      </button>
                      <div className="h-px bg-slate-200 my-1"></div>
                      <button
                        onClick={async () => { setProfileMenuOpen(false); await logout(); navigate("/login"); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        data-testid="logout-dropdown"
                      >
                        <LogOut className="w-4 h-4 text-red-500 shrink-0" /> Keluar
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>

        <footer className="px-8 py-4 text-xs text-slate-500 border-t border-slate-200 bg-white print:hidden">
          KEDAN MBG · v1.3 · © {new Date().getFullYear()} Pemprov Sumatera Utara powered by Edukarir Center
        </footer>
      </div>

      <ProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
      <PasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
    </div>
  );
}

